import { showToastNotification } from "@Reducers/toastSlice";
import {
  impactDataSource,
  severityDataSourceCustomSla,
  supportTicketTypes,
  supportTicketTypesToIdMapper,
} from "@Helpers/jiraConstants";
import { TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "@i18n";
import { untranslated, type TranslatedString } from "@i18n";
import { useDispatch, useSelector } from "react-redux";
import type { RootState } from "@Reducers/index";
import { getBackendUrl } from "@Utils";
import { replaceBase64WithTempKeys, extractTempKeysFromHtml } from "@Helpers/htmlUtils";
import _ from "lodash";
import { jiraController } from "../api/jira.api";

interface TicketDetails {
  summary: string;
  description: string;
  impact: string;
  severity: string;
  link: string;
  attachments: any[];
  createOnBehalfOf: { portalId: string; userId: string } | null;
  visibleToPortalUsers: boolean;
  visibleToOrgUsers: boolean;
}

interface PortalForUser {
  _id: string;
  name: string;
}

export interface UserForTicketCreation {
  _id: string;
  fullName: string;
  email: string;
  portals: PortalForUser[];
}

interface TicketCreationFormState {
  type?: string | null;
  status: boolean;
}

interface UseJiraTicketsProps {
  referrerUrl?: string;
  section?: string | null;
  component: Record<string, any>;
  onSaveSucessfull?: () => void;
  closeTicketCreationModalCallback?: (section?: string) => void;
  convertHtmlToAdf?: (html: string) => any;
  allUsers?: UserForTicketCreation[];
  source?: "Mobile" | "Web";
}
interface GenericEvent<T = any> {
  target?: {
    name?: keyof TicketDetails;
    value?: T;
    checked?: boolean;
    files?: any[];
  };
  [key: string]: any;
}

export const supportTicketTypeIcons: Record<string, string> = {
  [supportTicketTypes.REPORT_A_BUG]: "bugReport",
  [supportTicketTypes.SUGGEST_A_FEATURE]: "suggestAFeature",
  [supportTicketTypes.ASK_QUESTION]: "askAQuestion",
};

export const issueTypeIcons: Record<string, string> = Object.fromEntries(
  Object.entries(supportTicketTypeIcons).map(([type, icon]) => [supportTicketTypesToIdMapper[type], icon]),
);

export type CreateTicketMenuItem = {
  id: string;
  text: string;
  iconName: string;
};

function useJiraTickets({
  referrerUrl,
  section,
  component,
  onSaveSucessfull,
  closeTicketCreationModalCallback,
  convertHtmlToAdf,
  allUsers = [],
  source = "Web",
}: UseJiraTicketsProps) {
  const { t } = useTranslation();
  const portalData = useSelector((state: RootState) => state.portalData);
  const customSupportSlaEnabled = Boolean(portalData?.customSupportSla);
  const getInitialFields = (useReferrerUrl = false): TicketDetails => ({
    summary: "",
    description: "",
    impact: "",
    severity: "",
    link: useReferrerUrl ? (referrerUrl ?? "") : "",
    attachments: [],
    createOnBehalfOf: null,
    visibleToPortalUsers: true,
    visibleToOrgUsers: true,
  });

  const fileInputRef = useRef<any>(null);

  const [ticketDetails, setTicketDetails] = useState<TicketDetails>(getInitialFields(!!section));
  const [createOnBehalfOfOtherUser, setCreateOnBehalfOfOtherUser] = useState<boolean>(false);
  const [isCurrentUserWhiteListed, setIsCurrentUserWhiteListed] = useState<boolean>(false);
  const [showTicketCreationForm, setShowTicketCreationForm] = useState<TicketCreationFormState>({
    type: section,
    status: !!section,
  });
  const [showCreationLoader, setShowCreationLoader] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<UserForTicketCreation | null>(null);
  const [selectedPortal, setSelectedPortal] = useState<PortalForUser | null>(null);

  const dispatch = useDispatch();

  const isDisabledSaveButton =
    !ticketDetails.summary.trim() ||
    !ticketDetails.description.trim() ||
    (showTicketCreationForm.type === supportTicketTypes.REPORT_A_BUG &&
      (customSupportSlaEnabled ? !ticketDetails.severity : !ticketDetails.impact));

  const ticketHeaderInfo = {
    [supportTicketTypes.ASK_QUESTION]: {
      title: t("Ask a Question"),
      header: t(
        "Use this form to ask any kind of query, question or doubt you may have. It could be related to how to use a certain feature or anything in general like information about pricing.",
      ),
      svgName: supportTicketTypeIcons[supportTicketTypes.ASK_QUESTION],
    },
    [supportTicketTypes.REPORT_A_BUG]: {
      title: t("Report a Bug"),
      header: t("Use this form to report a bug in the software."),
      svgName: supportTicketTypeIcons[supportTicketTypes.REPORT_A_BUG],
    },
    [supportTicketTypes.SUGGEST_A_FEATURE]: {
      title: t("Suggest a Feature"),
      header: t(
        "Use this form to suggest an idea for a new feature or module. Every suggestion is reviewed by our Product team and if accepted may be reflected on the roadmap.",
      ),
      svgName: supportTicketTypeIcons[supportTicketTypes.SUGGEST_A_FEATURE],
    },
  };

  const createTicketMenuItems: CreateTicketMenuItem[] = Object.entries(ticketHeaderInfo).map(([id, info]) => ({
    id,
    text: info.title,
    iconName: info.svgName ?? "",
  }));

  const handleDrop = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault?.();
    const droppedFiles = Array.from(e?.dataTransfer?.files ?? []);
    setTicketDetails((prev) => ({
      ...prev,
      attachments: [...prev.attachments, ...droppedFiles],
    }));
  };

  useEffect(() => {
    setShowTicketCreationForm({ type: section, status: !!section });
  }, [section]);

  const closeTicketCreationModal = () => {
    setShowTicketCreationForm({ type: undefined, status: false });
    setTicketDetails(getInitialFields(false));
    setShowCreationLoader(false);
    setCreateOnBehalfOfOtherUser(false);
    setSelectedUser(null);
    setSelectedPortal(null);
    closeTicketCreationModalCallback?.(section ?? undefined);
  };

  const handleSaveBugDetails = async () => {
    setShowCreationLoader(true);
    const formData = new FormData();

    const ticketData = {
      data: {
        summary: ticketDetails.summary,
        descriptionString: replaceBase64WithTempKeys(
          ticketDetails.description,
          (tempKey) => `${getBackendUrl()}/fileSystemServer/getCommentImage/${tempKey}?type=jira`,
        ),
        imageTempKeys: extractTempKeysFromHtml(ticketDetails.description),
        ...(customSupportSlaEnabled ? { severity: ticketDetails.severity } : { impact: ticketDetails.impact }),
        link: ticketDetails.link,
        issuetype: _.get(supportTicketTypesToIdMapper, `${showTicketCreationForm.type}`),
        visibleToOrgUsers: ticketDetails.visibleToOrgUsers,
        visibleToPortalUsers: ticketDetails.visibleToPortalUsers,
        createOnBehalfOf: ticketDetails.createOnBehalfOf,
      },
      action: "createTicket",
      source,
    };

    for (const file of ticketDetails.attachments ?? []) {
      formData.append("uploadFiles", file);
    }

    formData.append("value", JSON.stringify(ticketData));

    try {
      const res = (await jiraController(formData)) as any;
      const body = res.data;

      if (body.status) {
        closeTicketCreationModal();
        dispatch(
          showToastNotification({
            content: (convertHtmlToAdf
              ? t("jiraTicketCreation", { ticket_number: body?.result?.key })
              : t("Ticket {{ticket}} has been created successfully", {
                  ticket: body?.result?.key,
                })) as TranslatedString,
            type: TOAST_TYPE_SUCCESS,
          }),
        );
        onSaveSucessfull?.();
      } else {
        dispatch(
          showToastNotification({
            content: untranslated(body.errorMessage) as TranslatedString,
            type: TOAST_TYPE_ERROR,
          }),
        );
      }
    } catch (error: any) {
      dispatch(
        showToastNotification({
          content: untranslated(error?.response?.data?.errorMessage ?? error?.message) as TranslatedString,
          type: TOAST_TYPE_ERROR,
        }),
      );
    } finally {
      setShowCreationLoader(false);
    }
  };

  const handleVisibiltyChange = (args: GenericEvent<boolean>, key: string) => {
    const objToUpdate: Record<string, boolean> = { [key]: args?.target?.checked ?? false };
    if (key === "visibleToPortalUsers" && args?.target?.checked) {
      objToUpdate.visibleToOrgUsers = true;
    } else if (key === "visibleToOrgUsers" && !args?.target?.checked) {
      objToUpdate.visibleToPortalUsers = false;
    }
    setTicketDetails((prev) => ({ ...prev, ...objToUpdate }));
  };

  const handleSelectUser = (_: unknown, newValue: UserForTicketCreation | null) => {
    setSelectedUser(newValue);

    if (!newValue || newValue.portals.length === 0) {
      setSelectedPortal(null);
      setTicketDetails((prev) => ({ ...prev, createOnBehalfOf: null }));
      return;
    }

    const portal: any = newValue.portals[0];
    setSelectedPortal(portal);
    setTicketDetails((prev) => ({
      ...prev,
      createOnBehalfOf: { portalId: portal._id, userId: newValue._id },
    }));
  };

  const handleSelectPortal = (_: unknown, newValue: PortalForUser | null) => {
    setSelectedPortal(newValue);
    setTicketDetails((prev) => ({
      ...prev,
      createOnBehalfOf: selectedUser && newValue ? { portalId: newValue._id, userId: selectedUser._id } : null,
    }));
  };

  const handleCreateOnBehalfOfChange = (args: GenericEvent<boolean>) => {
    const checked = args?.target?.checked ?? false;
    setCreateOnBehalfOfOtherUser(checked);
    if (!checked) {
      setSelectedUser(null);
      setSelectedPortal(null);
    }
    setTicketDetails((prev) => ({
      ...prev,
      createOnBehalfOf: checked ? prev.createOnBehalfOf : null,
    }));
  };

  const handleOnChange = (args: GenericEvent<string>) => {
    const name = args?.target?.name ?? "";
    const value = args?.target?.value ?? "";
    setTicketDetails((prev) => ({ ...prev, [name]: value }));
  };

  const handleRemoveFile = (fileName: string) => {
    setTicketDetails((prev) => ({
      ...prev,
      attachments: prev.attachments.filter((file: any) => file.name !== fileName),
    }));
  };

  const handleFileSelect = (e: GenericEvent<File[]>) => {
    const selectedFiles = Array.from(e?.target?.files ?? []);
    setTicketDetails((prev) => ({
      ...prev,
      attachments: [...prev.attachments, ...selectedFiles],
    }));
  };

  const ticketCreationFields = {
    fields: [
      ...(isCurrentUserWhiteListed
        ? [
            {
              heading: {
                text: t("Create on behalf of someone else"),
                allowIn: [
                  supportTicketTypes.REPORT_A_BUG,
                  supportTicketTypes.SUGGEST_A_FEATURE,
                  supportTicketTypes.ASK_QUESTION,
                ],
              },
              component: {
                component: component.createOnBehalfComopnent({
                  createOnBehalfOfOtherUser,
                  handleCreateOnBehalfOfChange,
                  t,
                }),
                allowIn: [
                  supportTicketTypes.REPORT_A_BUG,
                  supportTicketTypes.SUGGEST_A_FEATURE,
                  supportTicketTypes.ASK_QUESTION,
                ],
              },
            },
          ]
        : []),
      ...(createOnBehalfOfOtherUser
        ? [
            {
              heading: {
                text: t("Select user"),
                allowIn: [
                  supportTicketTypes.REPORT_A_BUG,
                  supportTicketTypes.SUGGEST_A_FEATURE,
                  supportTicketTypes.ASK_QUESTION,
                ],
              },
              component: {
                component: component.selectUserComponent({
                  selectedUser,
                  handleSelectUser,
                  allUsers,
                  t,
                }),
                allowIn: [
                  supportTicketTypes.REPORT_A_BUG,
                  supportTicketTypes.SUGGEST_A_FEATURE,
                  supportTicketTypes.ASK_QUESTION,
                ],
              },
            },
          ]
        : []),
      ...(createOnBehalfOfOtherUser && selectedUser
        ? [
            {
              heading: {
                text: t("Select portal"),
                allowIn: [
                  supportTicketTypes.REPORT_A_BUG,
                  supportTicketTypes.SUGGEST_A_FEATURE,
                  supportTicketTypes.ASK_QUESTION,
                ],
              },
              component: {
                component: component.selectPortalComponent({
                  handleSelectPortal,
                  portals: selectedUser.portals,
                  selectedPortal,
                  disabled: selectedUser.portals.length === 1,
                  t,
                }),
                allowIn: [
                  supportTicketTypes.REPORT_A_BUG,
                  supportTicketTypes.SUGGEST_A_FEATURE,
                  supportTicketTypes.ASK_QUESTION,
                ],
              },
            },
          ]
        : []),
      {
        heading: {
          text: t("Summary*"),
          allowIn: [
            supportTicketTypes.REPORT_A_BUG,
            supportTicketTypes.SUGGEST_A_FEATURE,
            supportTicketTypes.ASK_QUESTION,
          ],
        },
        component: {
          component: component.summaryComponent({
            ticketDetails,
            handleOnChange,
            placeholder: t("Enter Summary"),
            t,
          }),
          allowIn: [
            supportTicketTypes.REPORT_A_BUG,
            supportTicketTypes.SUGGEST_A_FEATURE,
            supportTicketTypes.ASK_QUESTION,
          ],
        },
      },
      {
        heading: {
          text: t("Description*"),
          allowIn: [
            supportTicketTypes.REPORT_A_BUG,
            supportTicketTypes.SUGGEST_A_FEATURE,
            supportTicketTypes.ASK_QUESTION,
          ],
        },
        subHeading: {
          text: t(
            "Describe the activity which you were performing when this bug occurred. Also specify which browser and OS on which this occurred. Any additional details on how we could replicate the issue.",
          ),
          allowIn: [supportTicketTypes.REPORT_A_BUG],
        },
        component: {
          component: component.descriptionComponent({
            maxLength: 2000,
            ticketDetails,
            handleOnChange,
            placeholder: t("Type something"),
            t,
          }),
          allowIn: [
            supportTicketTypes.REPORT_A_BUG,
            supportTicketTypes.SUGGEST_A_FEATURE,
            supportTicketTypes.ASK_QUESTION,
          ],
        },
      },
      {
        heading: {
          text: t("Attachment"),
          allowIn: [
            supportTicketTypes.REPORT_A_BUG,
            supportTicketTypes.SUGGEST_A_FEATURE,
            supportTicketTypes.ASK_QUESTION,
          ],
        },
        component: {
          component: component.attachmentComponent({
            handleFileSelect,
            ticketDetails,
            handleRemoveFile,
            handleDrop,
            fileInputRef,
            t,
          }),
          allowIn: [
            supportTicketTypes.REPORT_A_BUG,
            supportTicketTypes.SUGGEST_A_FEATURE,
            supportTicketTypes.ASK_QUESTION,
          ],
        },
      },
      customSupportSlaEnabled
        ? {
            heading: {
              text: t("Severity*"),
              allowIn: [supportTicketTypes.REPORT_A_BUG],
            },
            subHeading: {
              text: t("How severely does this issue affect your ability to use the system?"),
              allowIn: [supportTicketTypes.REPORT_A_BUG],
            },
            component: {
              component: component.severityComponent({
                severityDataSource: severityDataSourceCustomSla(t),
                ticketDetails,
                placeholder: t("Select a value"),
                handleOnChange,
                t,
              }),
              allowIn: [supportTicketTypes.REPORT_A_BUG],
            },
          }
        : {
            heading: {
              text: t("Impact*"),
              allowIn: [supportTicketTypes.REPORT_A_BUG],
            },
            subHeading: {
              text: t(
                "Is the bug only affecting a single or large number of users? Can it be replicated in different situations or just occurring in a specific use case.",
              ),
              allowIn: [supportTicketTypes.REPORT_A_BUG],
            },
            component: {
              component: component.impactComponent({
                impactDataSource: impactDataSource(t),
                ticketDetails,
                placeholder: t("Select a value"),
                handleOnChange,
                t,
              }),
              allowIn: [supportTicketTypes.REPORT_A_BUG],
            },
          },
      ...(component.linkUrlComponent
        ? [
            {
              heading: {
                text: t("URL or Link"),
                allowIn: [supportTicketTypes.REPORT_A_BUG, supportTicketTypes.ASK_QUESTION],
              },
              subHeading: {
                text: t(
                  "Please enter the URL or link to which this bug is related. You can copy the same from the CUBE browser tab. Providing the exact link on which this bug is occurring will help us troubleshoot it faster.",
                ),
                allowIn: [supportTicketTypes.REPORT_A_BUG],
              },
              component: {
                component: component.linkUrlComponent({
                  ticketDetails,
                  handleOnChange,
                  placeholder: t("Enter Link"),
                  t,
                }),
                allowIn: [supportTicketTypes.REPORT_A_BUG, supportTicketTypes.ASK_QUESTION],
              },
            },
          ]
        : []),
      {
        heading: {
          text: t("Visibility"),
          allowIn: [
            supportTicketTypes.REPORT_A_BUG,
            supportTicketTypes.ASK_QUESTION,
            supportTicketTypes.SUGGEST_A_FEATURE,
          ],
        },
        subHeading: {
          text: t("Control visibility of your tickets"),
          allowIn: [
            supportTicketTypes.REPORT_A_BUG,
            supportTicketTypes.ASK_QUESTION,
            supportTicketTypes.SUGGEST_A_FEATURE,
          ],
        },
        component: {
          component: component.visibilityComponent({
            handleVisibiltyChange,
            ticketDetails,
            t,
          }),
          allowIn: [
            supportTicketTypes.REPORT_A_BUG,
            supportTicketTypes.ASK_QUESTION,
            supportTicketTypes.SUGGEST_A_FEATURE,
          ],
        },
      },
    ],
  };

  return {
    ticketCreationFields,
    ticketDetails,
    setTicketDetails,
    createOnBehalfOfOtherUser,
    setCreateOnBehalfOfOtherUser,
    handleCreateOnBehalfOfChange,
    handleSelectUser,
    handleSelectPortal,
    handleOnChange,
    isCurrentUserWhiteListed,
    setIsCurrentUserWhiteListed,
    handleRemoveFile,
    handleFileSelect,
    handleVisibiltyChange,
    getInitialFields,
    showTicketCreationForm,
    setShowTicketCreationForm,
    handleSaveBugDetails,
    showCreationLoader,
    setShowCreationLoader,
    closeTicketCreationModal,
    isDisabledSaveButton,
    ticketHeaderInfo,
    createTicketMenuItems,
    selectedUser,
    setSelectedUser,
    selectedPortal,
    setSelectedPortal,
  };
}

export default useJiraTickets;
